import React from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';

import { SR_API_ACTION_UTILITY_USER_SMS_CODE_SIGNUP } from 'api/utilityActionTypes';

import { initialApiActionState } from 'api/createApiClient';

import { createInitialValues, createFormValidationFn } from 'client/helpers/formikHelpers';

import useBranch from 'client/hooks/useBranch';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrBox from 'client/components/SrBox';
import SrErrorMessage, {
  SR_ERROR_MESSAGE_THEME_RED,
} from 'client/components/SrErrorMessage/SrErrorMessage';
import {
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_THEME_ADOBE_GREY,
} from 'client/components/SrButton/SrButton';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';

const getFormFields = ({ codeSent, onClickResendCode }) => {
  return [
    {
      id: 'code',
      type: 'verification_code',
      title: 'Access Code',
      titleToggle: {
        text: codeSent ? 'Sent now!' : 'Resend code',
        onClick: onClickResendCode,
      },
      properties: {
        placeholder: 'Enter the code here...',
        autoFocus: true,
      },
      validations: ['required'],
    },
  ];
};

const getErrorMessage = (error) => {
  return (
    (error.constraint === 'utilityusers_phone_unique' &&
      'There is already an account with that phone number.') ||
    (error.constraint === 'utilityusers_email_unique' &&
      'There is already an account with that email.') ||
    error.message
  );
};

const SrUtilitySetupLazySignupVerificationModal = ({
  apiError,
  codeSent,
  onSubmit,
  onClickResendCode,
  onClose,
}) => {
  const { utilityUserSmsCodeSignupApiState = initialApiActionState } = useBranch({
    utilityUserSmsCodeSignupApiState: [
      'apiState',
      SR_API_ACTION_UTILITY_USER_SMS_CODE_SIGNUP,
      'default',
    ],
  });

  const formFields = getFormFields({
    codeSent,
    onClickResendCode: onClickResendCode,
  });

  return (
    <Formik
      initialValues={createInitialValues({ formFields })}
      validate={createFormValidationFn(formFields)}
      onSubmit={onSubmit}
      render={(formikBag) => {
        return (
          <SrSimpleMessageModal
            underline
            maxWidth={375}
            messageClassName="noAlign"
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
            headline="We just texted you a code."
            message={
              <div>
                {formFields.map((formField) => (
                  <SrFormikField
                    key={formField.id}
                    formField={formField}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                ))}
                <SrBox marginTop="10px">
                  {apiError && (
                    <SrErrorMessage theme={SR_ERROR_MESSAGE_THEME_RED}>
                      {getErrorMessage(apiError)}
                    </SrErrorMessage>
                  )}
                </SrBox>
              </div>
            }
            srMultiButtons={[
              {
                buttonText: 'Confirm',
                onButtonClick: formikBag.handleSubmit,
                buttonTheme: SR_BUTTON_THEME_ADOBE_GREEN,
                disabled: utilityUserSmsCodeSignupApiState.inProgress,
                buttonId: 'srConfirmCodeButton',
              },
              {
                buttonText: 'Cancel',
                onButtonClick: onClose,
                buttonTheme: SR_BUTTON_THEME_ADOBE_GREY,
                disabled: utilityUserSmsCodeSignupApiState.inProgress,
              },
            ]}
          />
        );
      }}
    />
  );
};

SrUtilitySetupLazySignupVerificationModal.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

SrUtilitySetupLazySignupVerificationModal.displayName = 'SrUtilitySetupLazySignupVerificationModal';

export default SrUtilitySetupLazySignupVerificationModal;
